/**
 * NewOrderTab — cola códigos → supplyEngine resolve → criar CockpitOrder.
 * (= Cockpit.jsx original + botão Criar Encomenda)
 */
import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api.js';
import { actionIcon } from '../../lib/icons.jsx';
import { useTableProfile } from '../../hooks/useTableProfile.js';
import { DataTable } from '@shared/UI/DataTable';
import '@shared/UI/DataTable/DataTable.css';

const eur = (n) => new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(n || 0);
const STATUS_COLOR = {
  ok: 'green', ok_alternativa: 'blue', stockout: 'red',
  sem_preco: 'orange', sem_matching: 'gray', oferta: 'purple', erro: 'red',
};

const NewOrderTab = () => {
  const navigate = useNavigate();
  const [group, setGroup] = useState('SNS');
  const [customerId, setCustomerId] = useState('');
  const [paste, setPaste] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [creating, setCreating] = useState(false);
  const { layoutConfig, saveLayout } = useTableProfile('gencrm_neworder');

  const resolve = async () => {
    setLoading(true); setErr(null); setResult(null);
    try {
      const lines = paste.split('\n').map((l) => l.trim()).filter(Boolean).map((l) => {
        const m = l.split(/[;, \t]+/);
        return { code: m[0], qty: Number(m[1] || 0) };
      }).filter((l) => l.code);
      if (!lines.length) { setErr('Cole pelo menos uma linha (código;qty).'); return; }
      const r = await api.post('/supply/resolve', { group, customerId: customerId || null, lines });
      setResult(r.data);
    } catch (e) { setErr(e.response?.data?.message || e.message); }
    finally { setLoading(false); }
  };

  const createOrder = async () => {
    setCreating(true);
    try {
      const lines = (result.lines || [])
        .filter((l) => l.sap)
        .map((l, i) => ({
          catalog_id: null, // será resolvido no backend via SAP code
          input_code: l.code,
          quantity: l.qty,
          unit_price: l.price ?? 0,
          is_code_unrecognized: false,
        }));

      const r = await api.post('/orders', {
        customer_id: customerId || null,
        order_type: 'ZPHA',
        lines,
      });
      navigate(`/cockpit/orders/${r.data.id}`);
    } catch (e) {
      setErr(e.response?.data?.message || e.message);
    } finally {
      setCreating(false);
    }
  };

  const sapPaste = result
    ? result.lines.filter((l) => l.sap).map((l) =>
        `${l.sap}\t${l.qty_caixa != null ? Math.ceil(l.qty_caixa) : l.qty}`
      ).join('\n')
    : '';

  const columns = useMemo(() => [
    { key: 'code', label: 'Código', width: 110 },
    { key: 'qty', label: 'Qty', width: 70 },
    { key: 'sap', label: 'SAP', width: 110, cellClassName: 'gencrm_ws_primary', render: (l) => l.sap || '—' },
    { key: 'nome', label: 'Nome', width: 200, render: (l) => l.nome || '—' },
    { key: 'marca', label: 'Marca', width: 120, render: (l) => l.marca || '—' },
    { key: 'caixa_grande', label: 'Cx', width: 60, render: (l) => l.caixa_grande ? 'GR' : 'PQ' },
    { key: 'qty_caixa', label: 'Qty Cx', width: 80, render: (l) => l.qty_caixa != null ? Math.ceil(l.qty_caixa) : '—' },
    { key: 'price', label: 'Preço', width: 100, aggregate: 'sum', render: (l) => l.price != null ? eur(l.price) : '—' },
    { key: 'line_total', label: 'Total', width: 110, aggregate: 'sum', render: (l) => l.line_total != null ? eur(l.line_total) : '—' },
    { key: 'status', label: 'Estado', width: 120, render: (l) => (
      <span className={`gencrm_badge gencrm_badge--${STATUS_COLOR[l.status] || 'gray'}`}>{l.status}</span>
    ) },
    { key: 'alertas', label: 'Alertas', width: 180, render: (l) => (
      (l.alertas || []).map((a, j) => (
        <div key={j} className="gencrm_cockpit__inlinealert">{actionIcon('warn', 12)} {a}</div>
      ))
    ) },
  ], []);

  return (
    <div className="gencrm_cockpit_neworder">
      <div className="gencrm_app__page-filters">
        <div className="gencrm_app__page-filters-left">
          <select className="gencrm_app__filter-input" value={group} onChange={(e) => setGroup(e.target.value)}>
            <option value="SNS">Grupo: SNS</option>
            <option value="GERAIS">Gerais</option>
            <option value="DIALISES">Diálises</option>
            <option value="EXPORTACAO">Exportação</option>
          </select>
          <input
            className="gencrm_app__filter-input"
            placeholder="Customer ID (opcional)"
            value={customerId}
            onChange={(e) => setCustomerId(e.target.value)}
          />
        </div>
        <div className="gencrm_app__page-filters-right">
          <button className="gencrm_btn" onClick={resolve} disabled={loading}>
            {loading ? 'A resolver…' : 'Resolver encomenda'}
          </button>
        </div>
      </div>

      <div className="gencrm_cockpit__input">
        <label>Colar códigos do cliente + quantidades (código;qty por linha)</label>
        <textarea
          rows={8}
          className="gencrm_field_input"
          placeholder={'2707297;540\n100006;20\n10012339;100'}
          value={paste}
          onChange={(e) => setPaste(e.target.value)}
        />
      </div>

      {err && <div className="gencrm_ws_error">Erro: {err}</div>}

      {result && (
        <>
          <div className="gencrm_cockpit__summary">
            <span>Total: <strong>{eur(result.total)}</strong></span>
            <span className={`gencrm_badge gencrm_badge--${result.minOk ? 'green' : 'red'}`}>
              {result.minOk ? 'Mínimo OK' : `< ${result.minRequired}€`}
            </span>
            {result.pendingPrice && (
              <span className="gencrm_badge gencrm_badge--orange">Linhas sem preço</span>
            )}
          </div>

          {(result.alerts || []).map((a, i) => (
            <div key={i} className="gencrm_cockpit__alert">{actionIcon('warn', 14)} {a}</div>
          ))}

          <DataTable
            columns={columns}
            data={result.lines}
            rowKey={(l, i) => i}
            pageSize={20}
            density="compact"
            enableTotals
            emptyText="Sem linhas."
            exportPrefix="gencrm_neworder"
            layoutConfig={layoutConfig}
            onLayoutChange={saveLayout}
          />

          <div className="gencrm_cockpit__actions">
            <button
              className="gencrm_btn gencrm_btn--primary"
              onClick={createOrder}
              disabled={creating || !customerId}
              title={!customerId ? 'Customer ID obrigatório para criar encomenda' : ''}
            >
              {creating ? 'A criar…' : 'Criar Encomenda ▸'}
            </button>
          </div>

          <div className="gencrm_cockpit__sapzone">
            <label>Zona para colar em SAP (SAP ⇥ qty_caixa)</label>
            <textarea
              rows={5}
              readOnly
              value={sapPaste}
              className="gencrm_field_input"
              onClick={(e) => e.currentTarget.select()}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default NewOrderTab;
